import './CommunityCards.css';

// Shared rendering for the "this playing card was changed by a Power-Up" indicator — the
// value-change twin of the Peek/Peeper "eye" badge. A value-modifying Pup (Plus/Minus One,
// Suit Change, Super Suit, Set Ace, One Man's Trash, Magic Trick, Substitution) rewrites a
// card in place; the server sends { original, current, by, code } per change (stacking, so a
// card carries an ordered list). Used by HoleCards (hero's own hand, owner-only) and
// CommunityCards (the board, public).

const RANKS = '23456789TJQKA';
const SUIT_SYM = { h: '♥', d: '♦', c: '♣', s: '♠' };
const SUIT_COLORS = { h: '#f0245e', d: '#f0245e', c: '#191e4e', s: '#191e4e' };

// A compact badge + a full tooltip sentence for a single change. `code` (the Pup card that did
// it) disambiguates the cases a raw before/after token delta can't read: One Man's Trash and
// Set Ace turn a card into an Ace (an odd rank "delta"), Substitution swaps the whole card.
// Everything else reads cleanly from the tokens — a lone rank change is a signed step (Plus/
// Minus One), a lone suit change is the new suit (Suit Change / Super Suit / a Magic Trick suit
// action). Matches the user-requested "+1 played by {player}" phrasing for the rank case.
export const describeMod = (mod) => {
  const who = mod?.by || 'another player';
  const code = mod?.code;
  const o = mod?.original;
  const c = mod?.current;
  if (code === 'SET_ACE' || code === 'ONE_MANS_TRASH') {
    return { badge: 'A', tip: `Changed to an Ace by ${who}` };
  }
  if (code === 'SUBSTITUTION') {
    return { badge: '⇄', tip: `Board card replaced by ${who}` };
  }
  if (o && c && o.length >= 2 && c.length >= 2) {
    const oi = RANKS.indexOf(o[0]);
    const ci = RANKS.indexOf(c[0]);
    if (o[0] !== c[0] && o[1] === c[1] && oi >= 0 && ci >= 0) {
      // Rank shifted, suit kept — the signed cyclic step (A<->2 wraps to ±1, not ∓12).
      let d = ci - oi;
      if (d > 6) d -= 13;
      if (d < -6) d += 13;
      const s = `${d > 0 ? '+' : ''}${d}`;
      return { badge: s, tip: `${s} played by ${who}` };
    }
    if (o[0] === c[0] && o[1] !== c[1]) {
      const g = SUIT_SYM[c[1]] || c[1];
      return { badge: g, tip: `Suit changed to ${g} by ${who}` };
    }
    const g = SUIT_SYM[c[1]] || c[1];
    return { badge: '⇄', tip: `Changed to ${c[0]}${g} by ${who}` };
  }
  return { badge: '⇄', tip: `Card changed by ${who}` };
};

// A card's stacked changes, looked up by the card's IDENTITY.
//
// This used to be keyed by position and "self-heal" by comparing the last change's result to the
// live token there. That was unsound in both directions: any effect that shifted the hand (an
// Exchange, a Sacrifice) silently deleted the provenance of every card after the removed one, and
// if a shifted card happened to carry a matching token it kept the WRONG card's badges. Keyed by
// id there is nothing to heal — a card that left play simply has no position to render at.
// Returns the ordered mods list (oldest first, so mods[0].original is the card's true original),
// or null when this card was never changed.
export const cardModsFor = (modsByCardId, cardId) => {
  if (!modsByCardId || cardId == null) return null;
  const list = modsByCardId[String(cardId)];
  return list && list.length ? list : null;
};

// The row of change pills over a card (one per stacked change, in order). Corner overlay with a
// pure-CSS hover tooltip, exactly like the eye badge. stopPropagation keeps reading a tooltip
// (or pressing a pill) from toggling / picking / hold-revealing the card underneath.
export const CardModBadges = ({ mods }) => {
  if (!mods || !mods.length) return null;
  return (
    <div
      className="hole-card-mods"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {mods.map((mod, i) => {
        const { badge, tip } = describeMod(mod);
        return (
          <span className="hole-card-mod" key={i}>
            {badge}
            <span className="hole-card-mod-tip">{tip}</span>
          </span>
        );
      })}
    </div>
  );
};

// A STATIC copy of a card's ORIGINAL face, overlaid while the owner presses-and-holds the card
// (reuses the Suit-Change crossfade's `.card-face` layout via `.card-original-overlay`, without
// the dissolve animation). `fontStyle` lets a shrunk PLO fan size the glyphs to match.
export const OriginalCardFace = ({ token, fontStyle }) => {
  if (!token || token.length < 2) return null;
  const rank = token.slice(0, -1);
  const suit = token.slice(-1);
  const fs = fontStyle || (() => undefined);
  return (
    <div
      className="card-original-overlay card-face"
      style={{ color: SUIT_COLORS[suit] || '#191e4e' }}
      aria-hidden="true"
    >
      <div className="card-rank" style={fs(16)}>{rank}</div>
      <div className="card-suit" style={fs(14)}>{SUIT_SYM[suit] || suit}</div>
      <div className="card-rank-big" style={fs(36)}>{rank}</div>
    </div>
  );
};
